import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  VisuallyHidden,
} from '@mantine/core';
import { IconExternalLink, IconSearch } from '@tabler/icons-react';

import {
  CountryPill,
  DueCell,
  EscalateReportButton,
  RatingBadge,
  SortableTh,
  StatusBadge,
  TruncatingPill,
  type SortProps,
} from '@/components';
import { config } from '@/config/environment';
import { useAuth } from '@/contexts/AuthContext';
import { useReports, useSearchDraft, useTeams } from '@/hooks';
import { isClientAnalyst } from '@/types/auth';
import { ApprovalStatus, ReportStatus, ReportSummary } from '@/types/report';
import { SCREENING_RATING_FILTER_OPTIONS, UNRATED_FILTER_OPTION } from '@/types/screening-outcome';
import { formatMoney } from '@/utils';

const STATUS_COLORS: Record<ReportStatus, string> = {
  triage: 'yellow',
  pending_activation: 'orange',
  queued: 'gray',
  processing: 'blue',
  awaiting_fulfilment: 'indigo',
  processing_feedback: 'blue',
  awaiting_file_selection: 'violet',
  awaiting_profile_review: 'orange',
  awaiting_match_review: 'grape',
  in_qa: 'cyan',
  completed: 'green',
  failed: 'red',
  // Coarse client stages — the only values non-admins receive.
  submitted: 'yellow',
  in_progress: 'blue',
};

const STATUS_LABELS: Partial<Record<ReportStatus, string>> = {
  pending_activation: 'Pending activation',
  awaiting_fulfilment: 'With Network Partner',
  awaiting_file_selection: 'Awaiting file selection',
  // The one gate clients act on, so they receive it uncollapsed.
  awaiting_profile_review: 'Awaiting profile review',
  awaiting_match_review: 'Awaiting match review',
  // Admin-only: clients see this report as 'in_progress'.
  in_qa: 'In QA',
  processing_feedback: 'Processing feedback',
  submitted: 'Submitted',
  in_progress: 'In progress',
  completed: 'Complete',
};

const APPROVAL_BADGE: Record<ApprovalStatus, { label: string; color: string }> = {
  approved: { label: 'Approved', color: 'green' },
  pending: { label: 'Pending review', color: 'yellow' },
  revoked: { label: 'Approval revoked', color: 'red' },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return '-';
  }
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Full timestamp for the date cells' hover title — the visible cell is
 * date-only so the column stays one line. */
function formatDateTime(dateStr: string | null): string | undefined {
  if (!dateStr) {
    return undefined;
  }
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = !!user?.isAdministrator;

  const page = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const limit = Number.parseInt(searchParams.get('limit') ?? '10', 10);
  const search = searchParams.get('search') ?? '';
  const sort = searchParams.get('sort') ?? '-created_at';
  const status = searchParams.get('status') ?? '';
  const teamId = searchParams.get('team') ?? '';
  const rating = searchParams.get('rating') ?? '';

  const { reports, isLoading, error, total } = useReports({
    page,
    limit,
    search,
    sort,
    status,
    teamId,
    rating,
  });

  const hasFilters = Boolean(search || status || teamId || rating);

  const updateParams = useCallback(
    (updates: Record<string, string | number>, opts?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === '' || value === 0) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }
          return next;
        },
        opts?.replace ? { replace: true } : undefined
      );
    },
    [setSearchParams]
  );

  // Debounced draft → URL commit (replace, not push — Back should not
  // step through every settled search value). Also guarantees no
  // commit can fire after unmount, unlike the previous raw setTimeout.
  const [searchValue, setSearchValue] = useSearchDraft(search, (settled) =>
    updateParams({ search: settled, page: 1 }, { replace: true })
  );

  const handleSort = useCallback(
    (field: string) => {
      const currentField = sort.replace('-', '');
      const isDesc = sort.startsWith('-');
      let newSort: string;
      if (currentField === field) {
        newSort = isDesc ? field : `-${field}`;
      } else {
        newSort = `-${field}`;
      }
      updateParams({ sort: newSort });
    },
    [sort, updateParams]
  );

  const sortBy = sort.replace('-', '');
  const sortOrder = sort.startsWith('-') ? 'desc' : 'asc';
  const totalPages = Math.ceil(total / limit);

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={2}>Reports</Title>
          {!isAdmin && (
            // Reports from before the GTI Hub cutover live in the legacy
            // platform; give clients a one-click way back to them.
            <Button
              component="a"
              href={config.backofficeAppUrl}
              target="_blank"
              rel="noreferrer"
              variant="default"
              rightSection={<IconExternalLink size={16} />}
              data-testid="historic-reports-link"
            >
              Historic Reports
            </Button>
          )}
        </Group>

        {/* One concise filter row (sorting lives in the column headers,
            page size next to the pagination). Search flexes; the rest
            keep fixed compact widths so nothing wraps. */}
        <Group gap="xs" wrap="nowrap">
          <TextInput
            placeholder={
              isAdmin
                ? 'Search subject, requester, codename, report type or partner…'
                : 'Search reference, subject or report type…'
            }
            leftSection={<IconSearch size={16} />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 180 }}
          />
          <Select
            placeholder="Status"
            data={
              isAdmin
                ? [
                    { value: 'pending_activation', label: 'Pending activation' },
                    { value: 'queued', label: 'Queued' },
                    { value: 'processing', label: 'Processing' },
                    { value: 'processing_feedback', label: 'Processing feedback' },
                    { value: 'awaiting_fulfilment', label: 'With Network Partner' },
                    { value: 'awaiting_file_selection', label: 'Awaiting file selection' },
                    { value: 'awaiting_profile_review', label: 'Awaiting profile review' },
                    { value: 'awaiting_match_review', label: 'Awaiting match review' },
                    { value: 'in_qa', label: 'In QA' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'failed', label: 'Failed' },
                  ]
                : // Clients only ever see (and may only filter by) the
                  // five coarse client statuses.
                  [
                    { value: 'submitted', label: 'Submitted' },
                    { value: 'pending_activation', label: 'Pending activation' },
                    { value: 'in_progress', label: 'In progress' },
                    { value: 'awaiting_profile_review', label: 'Awaiting profile review' },
                    { value: 'completed', label: 'Complete' },
                  ]
            }
            value={status || null}
            onChange={(value) => updateParams({ status: value ?? '', page: 1 })}
            clearable
            w={170}
            style={{ flexShrink: 0 }}
          />
          {/* Screening ratings are admin-only for now — the API masks
            them for clients, so the filter would match nothing. */}
          {isAdmin && (
            <Select
              placeholder="Rating"
              aria-label="Filter by screening rating"
              data={[...SCREENING_RATING_FILTER_OPTIONS, UNRATED_FILTER_OPTION]}
              value={rating || null}
              onChange={(value) => updateParams({ rating: value ?? '', page: 1 })}
              clearable
              w={150}
              style={{ flexShrink: 0 }}
              data-testid="reports-rating-filter"
            />
          )}
          {isAdmin && (
            <AdminReportFilters
              teamId={teamId}
              onChange={(updates) => updateParams({ ...updates, page: 1 })}
            />
          )}
          {hasFilters && (
            <Button
              variant="subtle"
              size="compact-sm"
              c="dimmed"
              style={{ flexShrink: 0 }}
              onClick={() => {
                setSearchValue('');
                updateParams({ search: '', status: '', team: '', rating: '', page: 1 });
              }}
              data-testid="reports-clear-filters"
            >
              Clear
            </Button>
          )}
        </Group>

        <Card shadow="sm" padding="lg" radius="md">
          <ReportsCardContent
            reports={reports}
            isLoading={isLoading}
            error={error}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </Card>

        {total > 0 && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total} reports
            </Text>
            <Group gap="sm">
              <Select
                value={limit.toString()}
                onChange={(value) =>
                  updateParams({ limit: Number.parseInt(value ?? '10', 10), page: 1 })
                }
                data={[
                  { value: '10', label: '10 / page' },
                  { value: '25', label: '25 / page' },
                  { value: '50', label: '50 / page' },
                  { value: '100', label: '100 / page' },
                ]}
                w={110}
                size="xs"
                data-testid="reports-page-size"
              />
              {totalPages > 1 && (
                <Pagination
                  value={page}
                  onChange={(p) => updateParams({ page: p })}
                  total={totalPages}
                  size="sm"
                />
              )}
            </Group>
          </Group>
        )}
      </Stack>
    </Container>
  );
}

/** Admin-only list filters: team (exact match). Requester search lives in
 * the combined search box. URL-param owned by the page like every other
 * filter. */
function AdminReportFilters({
  teamId,
  onChange,
}: Readonly<{
  teamId: string;
  onChange: (updates: Record<string, string | number>) => void;
}>) {
  const { teams } = useTeams();

  return (
    <Select
      placeholder="Team"
      data={teams.map((t) => ({ value: String(t.id), label: t.name }))}
      value={teamId || null}
      onChange={(value) => onChange({ team: value ?? '' })}
      clearable
      searchable
      style={{ width: 180 }}
      data-testid="reports-filter-team"
    />
  );
}

function ReportsCardContent({
  reports,
  isLoading,
  error,
  sortBy,
  sortOrder,
  onSort,
}: Readonly<{ reports: ReportSummary[]; isLoading: boolean; error: Error | null } & SortProps>) {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdministrator;

  if (isLoading) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  if (error) {
    return (
      <Center h={300}>
        <Text c="red">{error.message}</Text>
      </Center>
    );
  }

  if (reports.length === 0) {
    return (
      <Center h={300}>
        <Text c="dimmed">No reports found.</Text>
      </Center>
    );
  }

  // Admins list every team's reports — those rows need their context
  // (team, requester) spelled out. The wrapper exists so the teams
  // lookup (an admin endpoint) is only ever fetched for admins.
  const sortProps = { sortBy, sortOrder, onSort };
  return isAdmin ? (
    <AdminReportsTable reports={reports} {...sortProps} />
  ) : (
    <ReportsTable reports={reports} {...sortProps} />
  );
}

function AdminReportsTable({
  reports,
  ...sortProps
}: Readonly<{ reports: ReportSummary[] } & SortProps>) {
  const { teams } = useTeams();
  const teamNameById = useMemo(() => new Map(teams.map((t) => [String(t.id), t.name])), [teams]);
  return <ReportsTable reports={reports} teamNameById={teamNameById} {...sortProps} />;
}

function ReportsTable({
  reports,
  teamNameById,
  sortBy,
  sortOrder,
  onSort,
}: Readonly<
  {
    reports: ReportSummary[];
    /** Present for admins — adds the Team / Requested-by columns. */
    teamNameById?: Map<string, string>;
  } & SortProps
>) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Client analysts never see money (the API nulls it too) — drop the
  // column rather than render a dash-filled one.
  const showPrice = !isClientAnalyst(user);
  // Screening ratings are admin-only for now (the API masks them for
  // clients) — same treatment: drop the column, not dash-fill it.
  const showRating = !!user?.isAdministrator;
  const adminColumns = teamNameById !== undefined;
  const sortProps = { sortBy, sortOrder, onSort };

  return (
    // The Card clips overflow — without a scroll container an auto-layout
    // table that outgrows it silently loses its right-hand columns. Wide
    // content scrolls INSIDE the card instead.
    <Table.ScrollContainer minWidth={1100}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {/* Clients identify rows by their own reference (codenames
              are admin-only and not shown on this list at all). */}
            {!adminColumns && <Table.Th>Reference</Table.Th>}
            <Table.Th>Report Type</Table.Th>
            <Table.Th>Subjects</Table.Th>
            {!adminColumns && <Table.Th>Jurisdictions</Table.Th>}
            {adminColumns && <Table.Th>Team</Table.Th>}
            <Table.Th>Requested by</Table.Th>
            <SortableTh label="Status" field="status" {...sortProps} />
            {showRating && <SortableTh label="Rating" field="screening_rating" {...sortProps} />}
            <Table.Th>Approval</Table.Th>
            {showPrice && <Table.Th>Price</Table.Th>}
            <Table.Th>Due</Table.Th>
            <SortableTh label="Created" field="created_at" {...sortProps} />
            <SortableTh label="Completed" field="completed_at" {...sortProps} />
            <Table.Th w={44}>
              <VisuallyHidden>Actions</VisuallyHidden>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {reports.map((report) => {
            const approvalBadge = report.approvalStatus
              ? APPROVAL_BADGE[report.approvalStatus]
              : null;
            return (
              <Table.Tr
                key={report.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/reports/${report.id}`)}
              >
                {!adminColumns && (
                  <Table.Td>
                    <Text
                      size="sm"
                      fw={500}
                      truncate
                      maw={140}
                      title={report.clientReference ?? undefined}
                    >
                      {report.clientReference ?? '—'}
                    </Text>
                  </Table.Td>
                )}
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {report.configName ?? 'Screening Report'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {/* A single long subject name must never stretch the
                    table — bounded pills truncate with a hover title. */}
                  <Group gap={4} wrap="wrap" maw={280}>
                    {report.subjects.map((s) => (
                      <TruncatingPill key={s.id} label={s.name} />
                    ))}
                  </Group>
                </Table.Td>
                {!adminColumns && (
                  <Table.Td>
                    {report.jurisdictions.length ? (
                      // Flag + code badges (full name on hover); the cap
                      // keeps the unresolved-name fallback pill from
                      // stretching this auto-layout column.
                      <Group gap={4} wrap="wrap" maw={180}>
                        {report.jurisdictions.map((j) => (
                          <CountryPill key={j} country={j} />
                        ))}
                      </Group>
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                )}
                {adminColumns && (
                  <Table.Td>
                    <Text
                      size="sm"
                      truncate
                      maw={140}
                      title={report.teamId ? teamNameById.get(report.teamId) : undefined}
                    >
                      {report.teamId ? (teamNameById.get(report.teamId) ?? report.teamId) : '—'}
                    </Text>
                  </Table.Td>
                )}
                <Table.Td>
                  <Text size="sm" truncate maw={180} title={report.requesterEmail ?? undefined}>
                    {report.requesterEmail ?? '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <StatusBadge
                    color={STATUS_COLORS[report.status]}
                    label={STATUS_LABELS[report.status] ?? report.status}
                  />
                </Table.Td>
                {showRating && (
                  <Table.Td>
                    {/* The dash is "not rated (yet)". */}
                    <RatingBadge rating={report.screeningRating ?? null} />
                  </Table.Td>
                )}
                <Table.Td>
                  {approvalBadge ? (
                    <StatusBadge color={approvalBadge.color} label={approvalBadge.label} />
                  ) : (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                {showPrice && (
                  <Table.Td>
                    <Text
                      size="sm"
                      c={formatMoney(report.price, report.currency) ? undefined : 'dimmed'}
                    >
                      {formatMoney(report.price, report.currency) ?? '—'}
                    </Text>
                  </Table.Td>
                )}
                <Table.Td>
                  <DueCell dueAt={report.dueAt} turnaroundTimeDays={report.turnaroundTimeDays} />
                </Table.Td>
                <Table.Td>
                  <Text
                    size="sm"
                    style={{ whiteSpace: 'nowrap' }}
                    title={formatDateTime(report.createdAt)}
                  >
                    {formatDate(report.createdAt)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text
                    size="sm"
                    style={{ whiteSpace: 'nowrap' }}
                    title={formatDateTime(report.completedAt)}
                  >
                    {formatDate(report.completedAt)}
                  </Text>
                </Table.Td>
                <Table.Td
                  onClick={(e) => {
                    // The row navigates on click; keep that off the Actions cell.
                    e.stopPropagation();
                  }}
                >
                  <EscalateReportButton report={{ id: report.id }} variant="light" iconOnly />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
